import React, { useMemo, useEffect, useState } from "react";
import {
  Table,
  Flex,
  useColorModeValue,
  Box,
  useDisclosure,
  HStack,
  Tooltip,
  IconButton,
  Icon,
  Select,
  TableContainer,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import Card from "components/Card/Card.js";
import {
  useGetPaginatedNotifications,
  useArchiveNotification,
} from "queries/useNotification";
import useDataTable from "hooks/useDataTable";
import { Search } from "components/ui/table/Search";
import { dynamicSort } from "utils/helpers";
import ArchiveConfirmationModal from "components/ArchiveConfirmationModal";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { FaBoxOpen, FaPlus } from "react-icons/fa6";
import { useForm } from "react-hook-form";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { RiEdit2Fill } from "react-icons/ri";
import { FaArchive } from "react-icons/fa";
import { Title } from "components/CellElements";
import { truncate } from "lib/utils";

function Notifications() {
  const {
    formMethods,
    table,
    archiveModal,
    navigate,
    bgButton,
  } = useNotifications();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Box>
            <Select
              w="140px"
              placeholder=""
              {...formMethods.register("type")}
              defaultValue={
                localStorage.getItem("notificationsType") || "Alert"
              }
            >
              <option value="Alert">Alert</option>
              <option value="Events">Events</option>
              <option value="Education">Education</option>
              <option value="Contacts">Contacts</option>
              <option value="Archived">Archived</option>
            </Select>
          </Box>
          <Tooltip label="Add New Notification" placement="top">
            <IconButton
              aria-label="add new notification"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={() => navigate("/notifications/send")}
            />
          </Tooltip>
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <ArchiveConfirmationModal {...archiveModal} />
      </Card>
    </Flex>
  );
}

export default Notifications;

const useNotifications = () => {
  const navigate = useNavigate();
  const archiveModal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("notificationsSearch") || "",
      type: localStorage.getItem("notificationsType") || "Alert",
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("notificationsLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const watchSearch = formMethods?.watch("search");
  const watchType = formMethods?.watch("type");

  const getNotifications = useGetPaginatedNotifications({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
    filters: {
      ...(watchType ? { type: watchType } : {}),
    },
  });
  const archiveNotification = useArchiveNotification({
    onSuccess: () => archiveModal.onClose(),
  });

  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getNotifications.data?.data?.notifications || [], [
    JSON.stringify(getNotifications.data),
  ]);

  const onDelete = (notification) => {
    setSelectedNotificationId(notification?.id);
    archiveModal.onOpen();
  };

  const onEdit = (notification) =>
    navigate(`/notifications/view/${notification?.id}`);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (notification) => notification.title,
        Cell: ({ value: title }) => <Title title={title} />,
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        id: "description",
        Header: "Description",
        accessor: (notification) => notification.description,
        Cell: ({ value: description }) => (
          <Basic text={truncate(description, 20)} />
        ),
      },
      {
        id: "users",
        Header: "Users",
        accessor: (notification) => notification.users,
        Cell: ({ value: users }) => (
          <Basic text={users?.map((u) => u.lastName)?.join(", ") || "-"} />
        ),
        disableSortBy: true,
      },
      {
        id: "teams",
        Header: "Teams",
        accessor: (notification) => notification.teams,
        Cell: ({ value: users }) => (
          <Basic text={users?.map((u) => u.name)?.join(", ") || "-"} />
        ),
        disableSortBy: true,
      },
      {
        id: "type",
        Header: "Type",
        accessor: (notification) => notification.type,
        Cell: ({ value: type }) => <Basic text={type} />,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view league contact"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
            <Tooltip
              label={watchType === "Archived" ? "Unarchive" : "Archive"}
              placement="top"
            >
              <IconButton
                aria-label="archive league contact"
                icon={
                  <Icon
                    as={watchType === "Archived" ? FaBoxOpen : FaArchive}
                    boxSize={4}
                  />
                }
                colorScheme={watchType === "Archived" ? "green" : "orange"}
                onClick={() => onDelete(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    [watchType]
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id = column?.replace("name", "title") || "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(
    () => getNotifications.data?.data?.totalPages || 0,
    [getNotifications.data]
  );

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getNotifications.isLoading,
  });

  useEffect(() => {
    getNotifications.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
      filters: {
        ...(watchType ? { type: watchType } : {}),
      },
    });

    localStorage.setItem(
      "notificationsPage",
      table.pagination.pageIndex.toString()
    );
    localStorage.setItem(
      "notificationsLimit",
      table.pagination.pageSize.toString()
    );

    if (watchSearch !== undefined) {
      localStorage.setItem("notificationsSearch", watchSearch || "");
    }

    if (watchType !== undefined) {
      console.log("watchType", watchType);
      localStorage.setItem("notificationsType", watchType || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    watchType,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id = s.id?.replace("name", "title") || "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    archiveModal: {
      ...archiveModal,
      onConfirmDelete: () =>
        archiveNotification.mutate({
          id: selectedNotificationId,
          value: watchType === "Archived" ? 0 : 1,
        }),
      isLoading: archiveNotification.isLoading,
      title:
        "Are you sure you want to " +
        (watchType === "Archived" ? "unarchive" : "archive") +
        " this notification?",
      value: watchType === "Archived" ? "Unarchive" : "Archive",
    },
  };
};
